import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Package, ImageOff, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../lib/api";

const PriceSearch = () => {
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  const searching = search.trim().length > 0;

  useEffect(() => {
    api.get("/catalog/summary").then((r) => setBrands(r.data.brands || [])).catch(() => {});
    inputRef.current?.focus();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/catalog", {
        params: { search: search.trim() || undefined, brand: brand || undefined, page: 1, limit: 100 },
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [search, brand]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const margin = (it) =>
    it.rate != null && it.mrp ? Math.round((1 - it.rate / it.mrp) * 100) : null;

  const Photo = ({ it, size }) =>
    it.image_path ? (
      <a href={fileUrl(it.image_path)} target="_blank" rel="noopener noreferrer">
        <img
          src={fileUrl(it.image_path)}
          alt={it.name || it.article_no}
          loading="lazy"
          className={`${size} rounded-md object-cover border border-slate-300 dark:border-slate-700 bg-white`}
        />
      </a>
    ) : (
      <div className={`${size} rounded-md border border-dashed border-slate-300 dark:border-slate-700 flex items-center justify-center`}>
        <ImageOff className="h-5 w-5 text-slate-400 dark:text-slate-600" />
      </div>
    );

  return (
    <div data-testid="price-search-page" className="space-y-4">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Price Search</h1>
        <p className="text-base text-slate-600 dark:text-slate-400">Article no, product name, anything — with photo, rate, MRP and margin.</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
        <input
          ref={inputRef}
          data-testid="price-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search article no or product name…"
          className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 pl-11 pr-4 py-3.5 text-lg text-slate-900 dark:text-slate-100 placeholder:text-slate-500 focus:border-emerald-500 dark:focus:border-emerald-500/60 focus:outline-none"
        />
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="price-search-brand-chips">
        <button
          data-testid="brand-chip-all"
          onClick={() => setBrand("")}
          className={`shrink-0 rounded-full px-4 py-1.5 text-base font-semibold transition-colors ${
            !brand
              ? "bg-emerald-500 text-emerald-950"
              : "border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400"
          }`}
        >
          All brands
        </button>
        {brands.map((b) => (
          <button
            key={b}
            data-testid={`brand-chip-${b}`}
            onClick={() => setBrand(b === brand ? "" : b)}
            className={`shrink-0 rounded-full px-4 py-1.5 text-base font-semibold transition-colors ${
              brand === b
                ? "bg-emerald-500 text-emerald-950"
                : "border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-400"
            }`}
          >
            {b}
          </button>
        ))}
      </div>

      <div className="text-base text-slate-600 dark:text-slate-400" data-testid="price-search-count">
        {loading ? "Searching…" : `${total} item${total === 1 ? "" : "s"}${brand ? ` · ${brand}` : ""}`}
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center" data-testid="price-search-loading">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-600 dark:text-emerald-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 py-16" data-testid="price-search-empty">
          <Package className="h-8 w-8 text-slate-400" />
          <p className="text-base text-slate-600 dark:text-slate-400">No items found. Upload a price list PDF in Capture first.</p>
        </div>
      ) : searching ? (
        <ul className="divide-y divide-slate-200 dark:divide-slate-700 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900" data-testid="price-search-list">
          {items.map((it) => (
            <li key={`${it.article_no}-${it.size}-${it.brand}`} data-testid={`search-row-${it.article_no}`} className="flex items-center gap-3 px-3 py-2.5">
              <Photo it={it} size="h-16 w-16 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-mono text-base font-semibold text-cyan-600 dark:text-cyan-400">{it.article_no}</span>
                  <span className="text-sm font-mono uppercase tracking-wider text-slate-500 dark:text-slate-400">{it.brand}</span>
                </div>
                <div className="truncate text-base text-slate-800 dark:text-slate-200">
                  {it.name || "—"}{it.size ? ` · ${it.size}` : ""}
                </div>
                <div className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
                  {it.updated_at ? `Price updated ${formatDistanceToNow(new Date(it.updated_at), { addSuffix: true })}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-lg font-bold text-emerald-600 dark:text-emerald-400">{it.rate != null ? `₹${it.rate}` : "—"}</div>
                <div className="font-mono text-sm text-slate-500 dark:text-slate-400">MRP {it.mrp != null ? `₹${it.mrp}` : "—"}</div>
                {margin(it) != null && (
                  <div className="font-mono text-sm font-semibold text-amber-600 dark:text-amber-400">{margin(it)}% margin</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="price-search-grid">
          {items.map((it) => (
            <div
              key={`${it.article_no}-${it.size}-${it.brand}`}
              data-testid={`search-card-${it.article_no}`}
              className="rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 space-y-2"
            >
              <div className="flex justify-center">
                <Photo it={it} size="h-32 w-32" />
              </div>
              <div>
                <div className="flex items-baseline justify-between gap-1">
                  <span className="font-mono text-base font-semibold text-cyan-600 dark:text-cyan-400 truncate">{it.article_no}</span>
                  <span className="text-sm font-mono uppercase tracking-wider text-slate-500 dark:text-slate-400 shrink-0">{it.brand}</span>
                </div>
                <div className="text-base text-slate-800 dark:text-slate-200 leading-snug line-clamp-2">
                  {it.name || "—"}{it.size ? ` · ${it.size}` : ""}
                </div>
              </div>
              <div className="flex items-end justify-between border-t border-slate-200 dark:border-slate-700 pt-2">
                <div>
                  <div className="font-mono text-xl font-bold text-emerald-600 dark:text-emerald-400">{it.rate != null ? `₹${it.rate}` : "—"}</div>
                  <div className="font-mono text-sm text-slate-500 dark:text-slate-400">MRP {it.mrp != null ? `₹${it.mrp}` : "—"}</div>
                </div>
                {margin(it) != null && (
                  <div className="rounded bg-amber-100 dark:bg-amber-500/20 px-1.5 py-0.5 font-mono text-sm font-bold text-amber-700 dark:text-amber-300">
                    {margin(it)}%
                  </div>
                )}
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                {it.updated_at ? `Updated ${formatDistanceToNow(new Date(it.updated_at), { addSuffix: true })}` : ""}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PriceSearch;
